/**
 * Dashboard DTO Mappers
 * 
 * Transforma os DTOs padronizados do backend (ProjectDashboardDto, CustomerDashboardDto, IdeaDashboardDto)
 * no formato esperado pelos componentes de UI do frontend.
 */

// ───── Project Dashboard Mapper ─────

export function mapProjectDashboard(dashboard: any) {
  if (!dashboard) return null;

  const statusMap: Record<string, string> = {
    PLANNED: 'Planejamento',
    ACTIVE: 'Em Andamento',
    ON_HOLD: 'Pausado',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
  };

  const priorityMap: Record<string, string> = {
    LOW: 'Baixa',
    MEDIUM: 'Média',
    HIGH: 'Alta',
    CRITICAL: 'Crítica',
  };

  const taskStatusMap: Record<string, string> = {
    TODO: 'TODO',
    IN_PROGRESS: 'IN_PROGRESS',
    REVIEW: 'REVIEW',
    DONE: 'DONE',
    BLOCKED: 'BLOCKED',
  };

  const formatDate = (date?: string | null) =>
    date
      ? new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
      : 'Não definido';

  const members = (dashboard.members || []).map((m: any) => ({
    id: m.id,
    name: m.name || 'Membro',
    email: m.email || '',
    role: m.role || 'Membro da Equipe',
    avatarUrl: m.avatarUrl || null,
    allocation: m.allocation || 100,
    initials: (m.name || 'M')
      .split(' ')
      .map((s: string) => s[0])
      .join('')
      .toUpperCase()
      .slice(0, 2),
  }));

  const kanbanTasks = dashboard.kanban
    ? [
        ...(dashboard.kanban.todo || []).map((t: any) => ({ ...t, status: t.status || 'TODO' })),
        ...(dashboard.kanban.inProgress || []).map((t: any) => ({ ...t, status: t.status || 'IN_PROGRESS' })),
        ...(dashboard.kanban.review || []).map((t: any) => ({ ...t, status: t.status || 'REVIEW' })),
        ...(dashboard.kanban.done || []).map((t: any) => ({ ...t, status: t.status || 'DONE' })),
        ...(dashboard.kanban.blocked || []).map((t: any) => ({ ...t, status: t.status || 'BLOCKED' })),
      ]
    : [];

  const taskSource = Array.isArray(dashboard.tasks) && dashboard.tasks.length > 0 ? dashboard.tasks : kanbanTasks;

  const tasks = (taskSource || []).map((t: any) => ({
    id: t.id,
    title: t.title,
    description: t.description || '',
    status: taskStatusMap[t.status] || t.status,
    priority: priorityMap[t.priority] || t.priority || 'Média',
    assigneeId: t.assigneeId || '',
    assigneeName: t.assigneeName || '',
    category: t.category || '',
    estimateHours: t.estimateHours || undefined,
    dueDate: t.dueDate ? new Date(t.dueDate).toISOString().split('T')[0] : '',
  }));

  return {
    ...dashboard,
    // Status normalizado para exibição
    statusLabel: statusMap[dashboard.status] || dashboard.status,
    // Prioridade do projeto (não vem do backend, mantém fallback)
    priority: priorityMap[dashboard.priority] || dashboard.priority || 'Média',
    // Progresso calculado (vem do backend) — normaliza número
    progress: Number(dashboard.progress || 0),
    // Budget
    budget: Number(dashboard.budget || 0),
    // Responsável (fallback quando não mapeado)
    owner: dashboard.owner || members[0]?.name || 'Não definido',
    // Nome da empresa/cliente
    company: dashboard.company || dashboard.customer?.name || '',
    // Data de entrega (usa endDate ISO → formato amigável)
    dueDate: formatDate(dashboard.endDate),
    startDate: dashboard.startDate ? formatDate(dashboard.startDate) : 'Não definido',
    // Members mapeados para o formato da UI de equipe
    members,
    // Tasks mapeadas
    tasks,
    // Documents mapeados como arquivos
    documents: (dashboard.documents || []).map((d: any) => ({
      id: d.id,
      name: d.filename,
      size: d.size || '0 KB',
      type: (d.mime?.split('/')?.pop() || 'file').toUpperCase(),
      uploader: d.uploadedBy || 'Sistema',
      date: d.createdAt ? new Date(d.createdAt).toLocaleDateString('pt-BR') : '',
    })),
    // Sprints
    sprints: (dashboard.sprints || []).map((s: any) => ({
      id: s.id,
      name: s.name,
      goal: s.goal || '',
      startDate: s.startDate || '',
      endDate: s.endDate || '',
      status: s.status,
    })),
    // Milestones (roadmap)
    milestones: (dashboard.milestones || []).map((m: any) => ({
      id: m.id,
      name: m.name,
      dueDate: m.dueDate ? new Date(m.dueDate).toLocaleDateString('pt-BR') : '',
      completedAt: m.completedAt || null,
    })),
    // History → atividades
    activities: (dashboard.history || []).map((h: any) => ({
      id: h.id,
      text: h.action,
      type: 'system',
      time: h.time ? new Date(h.time).toLocaleString('pt-BR') : '',
    })),
    // Customer info
    customer: dashboard.customer
      ? {
          id: dashboard.customer.id,
          name: dashboard.customer.name,
          industry: dashboard.customer.industry || '',
          status: dashboard.customer.status || 'ACTIVE',
        }
      : null,
    // Datas ISO
    createdAt: dashboard.createdAt,
    updatedAt: dashboard.updatedAt,
  };
}

// ───── Customer Dashboard Mapper ─────

export function mapCustomerDashboard(dashboard: any) {
  if (!dashboard) return null;

  const statusMap: Record<string, string> = {
    ACTIVE: 'Ativo',
    INACTIVE: 'Inativo',
    LEAD: 'Lead',
    PROSPECT: 'Prospect',
    CHURNED: 'Churned',
  };

  return {
    ...dashboard,
    // Status normalizado
    statusLabel: statusMap[dashboard.status] || dashboard.status || 'Ativo',
    // Contacts → team members
    contacts: (dashboard.contacts || []).map((c: any) => ({
      id: c.id,
      firstName: c.firstName,
      lastName: c.lastName || '',
      name: `${c.firstName} ${c.lastName || ''}`.trim(),
      email: c.email || '',
      phone: c.phone || '',
      role: c.role || 'Contato',
      initials: (c.firstName || 'C').charAt(0).toUpperCase(),
    })),
    // Projects
    projects: (dashboard.projects || []).map((p: any) => ({
      id: p.id,
      name: p.name,
      status: p.status,
      budget: p.budget || 0,
      endDate: p.endDate ? new Date(p.endDate).toLocaleDateString('pt-BR') : '',
      createdAt: p.createdAt || '',
      progress: p.progress || 0,
      owner: p.owner || 'Não definido',
      role: p.role || '',
    })),
    // Contracts
    contracts: (dashboard.contracts || []).map((c: any) => ({
      id: c.id,
      contractNumber: c.contractNumber || '',
      status: c.status,
      value: c.value || 0,
      startDate: c.startDate || '',
      endDate: c.endDate || '',
    })),
    // Documents
    documents: (dashboard.documents || []).map((d: any) => ({
      id: d.id,
      title: d.filename,
      type: (d.mime?.split('/')?.pop() || 'file').toUpperCase(),
      size: d.size || '0 KB',
      date: d.createdAt ? new Date(d.createdAt).toLocaleDateString('pt-BR') : '',
      category: 'Documento',
    })),
    // History → timeline
    history: (dashboard.history || []).map((h: any) => ({
      id: h.id,
      date: h.time ? new Date(h.time).toLocaleString('pt-BR') : '',
      title: h.action,
      desc: h.action,
      type: 'atividade',
      action: h.action,
      user: h.user,
      time: h.time,
    })),
    // Invoices → financial
    invoices: (dashboard.invoices || []).map((inv: any) => ({
      id: inv.id,
      invoiceNumber: inv.invoiceNumber,
      status: inv.status,
      total: inv.total || 0,
      dueAt: inv.dueAt || '',
      paidAt: inv.paidAt || '',
    })),
  };
}

// ───── Idea Dashboard Mapper ─────

export function mapIdeaDashboard(dashboard: any) {
  const payload = dashboard && typeof dashboard === 'object' && 'success' in dashboard && 'data' in dashboard
    ? dashboard.data
    : dashboard;

  if (!payload) return null;

  const statusMap: Record<string, string> = {
    CAPTURED: 'capturadas',
    ANALYSIS: 'avaliacao',
    REFINEMENT: 'pesquisa',
    BUSINESS_CASE: 'mvp',
    APPROVED: 'aprovadas',
    DEVELOPMENT: 'desenvolvimento',
    DELIVERED: 'entregues',
    ARCHIVED: 'arquivadas',
  };

  const priorityMap: Record<string, string> = {
    LOW: 'Baixa',
    MEDIUM: 'Média',
    HIGH: 'Alta',
    CRITICAL: 'Crítica',
  };

  return {
    ...payload,
    status: statusMap[payload.status] || payload.status || 'avaliacao',
    priority: priorityMap[payload.priority] || payload.priority || 'Média',
    creator: payload.author?.name || payload.createdBy || 'Criador',
    owner: payload.author?.name || 'Responsável',
    files: (payload.files || []).map((f: any) => ({
      id: f.id,
      name: f.name,
      size: f.size,
      type: f.type || 'file',
      url: f.url || '',
      addedBy: f.addedBy || 'Sistema',
      date: f.date || '',
    })),
    history: (payload.history || []).map((h: any) => ({
      id: h.id,
      action: h.action,
      user: h.user,
      time: h.time,
    })),
    comments: payload.comments || [],
    canvas: payload.canvas || {},
    score: payload.score || 0,
    createdAt: payload.createdAt || new Date().toISOString(),
    updatedAt: payload.updatedAt || new Date().toISOString(),
  };
}
